import React, { useState, useRef, useCallback, memo, useEffect } from "react";
import { useCart } from "../context/CartContext";
import SpiceSelector from "./SpiceSelector";
import { SpiceLevel } from "../types/menu";
import { useTheme } from "../context/ThemeContext";
import { CURRENCY } from '../config/currency';

// Item color mapping for modern theme - soft/subtle colors
const getItemColor = (name: string): { color: string; gradient: string } => {
  const lower = name.toLowerCase();
  if (lower.includes('chicken') || lower.includes('murgh')) return { color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' };
  if (lower.includes('mutton') || lower.includes('lamb')) return { color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' };
  if (lower.includes('fish') || lower.includes('prawn')) return { color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' };
  if (lower.includes('paneer')) return { color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' };
  if (lower.includes('biryani') || lower.includes('rice')) return { color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' };
  if (lower.includes('noodle') || lower.includes('pasta')) return { color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' };
  if (lower.includes('pizza')) return { color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' };
  if (lower.includes('burger')) return { color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' };
  if (lower.includes('soup')) return { color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' };
  if (lower.includes('salad')) return { color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' };
  if (lower.includes('dessert') || lower.includes('ice cream') || lower.includes('cake')) return { color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' };
  if (lower.includes('coffee')) return { color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' };
  if (lower.includes('tea')) return { color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' };
  if (lower.includes('juice') || lower.includes('shake')) return { color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' };
  // Default - soft green
  return { color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' };
};

interface Portion {
  id: number;      // Real database portion ID
  name: string;
  price: number;
  qty: number;
}

interface MenuItem {
  id: number;
  name: string;
  description: string;
  basePrice: number;
  portions: Portion[];
  allowsSpiceCustomization?: boolean;
  spiceLevel?: SpiceLevel;
}

interface SmartPortionDropdownProps {
  item: MenuItem;
  onClose: () => void;
}

// Module-level map to track which instance is the active one (survives React StrictMode double-mount)
const globalPrimaryInstances = new Map<number, string>(); // Track which instance is primary (by unique ID)

// Global SYNCHRONOUS lock to prevent React's synthetic event duplication
// This MUST be checked and set synchronously (no async operations between check and set)
const globalActiveLocks = new Map<string, boolean>(); // key: "itemId-portionIndex-delta", value: true if currently processing

const SmartPortionDropdown = ({ item, onClose }: SmartPortionDropdownProps) => {
  const { addToCart, getCartQuantity, removeFromCart, state, clearCart } = useCart();
  const { currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const itemColor = isModernTheme ? getItemColor(item.name) : null;

  // Generate unique instance ID (survives re-renders but not unmounts)
  const instanceId = useRef(Math.random().toString(36).substring(2, 15)).current;

  console.log("🎬 SmartPortionDropdown CONSTRUCTOR called - Instance:", instanceId);
  console.log("🎬 Item ID:", item?.id, "Item name:", item?.name);
  console.log("🎬 Initial state.items:", state?.items);
  console.log("🎬 Initial state.items.length:", state?.items?.length || 0);
  
  // Log full cart item details to see the doubled quantity
  if (state?.items && state.items.length > 0) {
    const cartDetails = state.items.map(item => ({
      menuItemId: item.menuItem.id,
      menuItemName: item.menuItemName,
      portionId: item.portionId,
      portionName: item.portionName,
      quantity: item.quantity,
      price: item.price
    }));
    console.log("🎬 Full cart item details:");
    cartDetails.forEach((item, idx) => {
      console.log(`  Item ${idx}: ${item.menuItemName} - ${item.portionName} - QTY: ${item.quantity}`);
    });
  }
  
  const [quantities, setQuantities] = useState<Portion[]>(() => {
    const initial = item.portions.map(p => ({ ...p, qty: 0 }));
    console.log("🎬 Initial quantities state:", initial);
    return initial;
  });
  // Single spice selection for the whole item (applies to all portions)
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState<SpiceLevel | null>(
    item.spiceLevel || null
  );
  const [renderKey, setRenderKey] = useState(0);
  const isInitializedRef = useRef(false);
  const pendingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Track pending setTimeout
  const itemRef = useRef(item);
  const addToCartRef = useRef(addToCart);
  const removeFromCartRef = useRef(removeFromCart);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [positionClass, setPositionClass] = useState("md:top-full");
  
  // Update refs on every render
  itemRef.current = item;
  addToCartRef.current = addToCart;
  removeFromCartRef.current = removeFromCart;

  // Viewport-aware positioning
  useEffect(() => {
    const checkPosition = () => {
      if (dropdownRef.current) {
        const dropdown = dropdownRef.current;
        const rect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Get parent container position
        const parent = dropdown.parentElement;
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          const spaceBelow = viewportHeight - parentRect.bottom;
          const spaceAbove = parentRect.top;
          const dropdownHeight = rect.height || 400; // Estimated height if not yet rendered

          console.log(`📐 Dropdown positioning check:`, {
            parentBottom: parentRect.bottom,
            parentTop: parentRect.top,
            spaceBelow,
            spaceAbove,
            dropdownHeight,
            viewportHeight
          });

          // If not enough space below, show above
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            console.log(`⬆️ Opening dropdown ABOVE (not enough space below)`);
            setPositionClass("md:bottom-full md:mb-2");
          } else {
            console.log(`⬇️ Opening dropdown BELOW (enough space or more space below)`);
            setPositionClass("md:top-full md:mt-2");
          }
        }
      }
    };

    // Check position on mount and when quantities change (affects height)
    checkPosition();

    // Recheck on window resize
    window.addEventListener('resize', checkPosition);
    window.addEventListener('scroll', checkPosition, true); // Capture phase for scrolling in containers

    return () => {
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('scroll', checkPosition, true);
    };
  }, [quantities]); // Re-check when quantities change (affects dropdown height)
  
  // Cleanup pending timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) {
        console.log("🧹 Cleaning up pending timeout on unmount");
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
  }, []);
  
  console.log("🎬 Current quantities:", quantities.map((p, i) => `${p.name}: ${p.qty}`));

  // Mark this instance as primary and initialize from cart
  React.useEffect(() => {
    const itemKey = item.id;
    console.log(`🔁 useEffect RUNNING for instance ${instanceId} - itemKey:`, itemKey);
    
    // Mark this instance as the primary one (latest mount wins)
    const currentPrimary = globalPrimaryInstances.get(itemKey);
    console.log(`🔍 Current primary instance for item ${itemKey}: ${currentPrimary}`);
    
    // Always set this instance as primary (latest mount wins in StrictMode)
    globalPrimaryInstances.set(itemKey, instanceId);
    console.log(`🏆 Instance ${instanceId} became PRIMARY for item ${itemKey}`);

    // Initialize quantities from cart
    console.log(`✅ Initializing from cart for item ${itemKey}`);
    console.log("🔍 CART STATE:", state);
    console.log("🔍 CART ITEMS COUNT:", state?.items?.length || 0);

    const existingQuantities = item.portions.map((portion, index) => {
      const cartItems = state?.items?.filter(cartItem =>
        cartItem.menuItem.id === item.id &&
        cartItem.portionId === portion.id  // ✅ Use real database portion ID, not array index
      ) || [];
      const cartQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      console.log(`📊 Portion ${portion.name} (id:${portion.id}): ${cartQty} in cart`);
      return { ...portion, qty: cartQty };
    });
    // Prefill selected spice level from any existing cart item or default
    const anySpice = (state?.items?.find(ci => ci.menuItem.id === item.id && ci.requestedSpiceLevel)?.requestedSpiceLevel) || (item.spiceLevel || null);
    setSelectedSpiceLevel(anySpice);
    
    console.log("📊 Setting quantities:", existingQuantities.map(q => `${q.name}: ${q.qty}`));
    setQuantities(existingQuantities);
    isInitializedRef.current = true;

    // Cleanup: remove this instance as primary when unmounting
    return () => {
      console.log(`🧹 Cleanup for instance ${instanceId} - item ${itemKey}`);
      // Only delete if this instance is still the primary
      if (globalPrimaryInstances.get(itemKey) === instanceId) {
        globalPrimaryInstances.delete(itemKey);
        console.log(`🧹 Removed primary instance ${instanceId} for item ${itemKey}`);
      }
    };
  }, []); // Empty dependency - run once per mount

  // Note: Removed the useEffect that syncs from cart to local state
  // This was causing the double-adding issue due to circular updates

  const handleQtyChange = useCallback((index: number, delta: number) => {
    // ✅ SYNCHRONOUS LOCK - Must be checked and set in the same tick
    // This prevents React's duplicate synthetic event handlers from both executing
    const clickKey = `${itemRef.current.id}-${index}-${delta}`;
    
    console.log(`🔥 Button clicked: portion ${index}, delta ${delta}, instance: ${instanceId}, key: ${clickKey}`);
    
    // Check if this exact action is already being processed RIGHT NOW
    if (globalActiveLocks.get(clickKey) === true) {
      console.log(`🚫 BLOCKED: Already processing ${clickKey} - duplicate synthetic event from React StrictMode`);
      return;
    }
    
    // Immediately acquire lock (synchronously, before ANY other operations)
    globalActiveLocks.set(clickKey, true);
    console.log(`🔒 LOCK ACQUIRED for ${clickKey}`);
    
    // Release lock after a short delay (allows the current execution to complete)
    setTimeout(() => {
      globalActiveLocks.delete(clickKey);
      console.log(`🔓 LOCK RELEASED for ${clickKey}`);
    }, 150);
    
    // Guard against double execution during initial renders
    if (!isInitializedRef.current) {
      console.log("⚠️ Component not fully initialized, skipping qty change");
      globalActiveLocks.delete(clickKey); // Release lock immediately
      return;
    }
    
    console.log("✅ Processing click");
    
    // Clear any pending timeout to prevent double execution
    if (pendingTimeoutRef.current) {
      console.log("🧹 Clearing previous pending timeout");
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    
    let newQuantities: Portion[];
    setQuantities(prev => {
      console.log(`📊 BEFORE UPDATE - All quantities:`, prev.map((p, i) => `${p.name}: ${p.qty}`));
      
      const updated = [...prev];
      const oldQty = updated[index].qty;
      const newQty = Math.max(0, oldQty + delta);
      updated[index].qty = newQty;
      
      console.log(`🔄 Qty change for portion ${index} (${updated[index].name}): ${oldQty} + ${delta} = ${newQty}`);
      console.log(`📊 AFTER UPDATE - All quantities:`, updated.map((p, i) => `${p.name}: ${p.qty}`));
      
      newQuantities = updated; // Capture updated quantities
      return updated;
    });
    
    // Schedule cart update for next tick to avoid "Cannot update during render" warning
    pendingTimeoutRef.current = setTimeout(() => {
      console.log(`🚀 Executing cart update`);
      pendingTimeoutRef.current = null;
      
      // Replace all items for this menu item with the new quantities
      console.log(`🗑️ Removing all items for menu item ${itemRef.current.id} from cart`);
      removeFromCartRef.current(itemRef.current.id);
      
      // Then add all portions with their current quantities
      console.log(`📦 Adding updated quantities to cart:`);
      newQuantities!.forEach((p, idx) => {
        if (p.qty > 0) {
          console.log(`  ➕ Portion ${idx} (${p.name}): adding ${p.qty} item(s) at ${CURRENCY.symbol}${p.price} each, portionId: ${p.id}`);
          addToCartRef.current({
            id: itemRef.current.id,
            price: p.price,
            portionId: p.id,  // ✅ Use REAL database ID, not array index
            portionName: p.name,
            requestedSpiceLevel: selectedSpiceLevel || undefined
          }, p.qty);
        }
      });
    }, 0);
    
    // Force re-render
    setRenderKey(prev => prev + 1);
    
    console.log("✅ Click processed successfully");
  }, []); // NO dependencies - function never recreated!

  // Store handleQtyChange in a ref so click handlers don't need it as dependency
  const handleQtyChangeRef = useRef(handleQtyChange);
  handleQtyChangeRef.current = handleQtyChange;

  // Memoized click handlers to prevent recreation on every render
  const handleMinusClick = useCallback((index: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any default behavior
    
    const clickKey = `${itemRef.current.id}-${index}--1`;
    console.log(`🔥 Minus button clicked for portion ${index}, key: ${clickKey}`);
    
    // Check lock IMMEDIATELY, before any async operations
    if (globalActiveLocks.get(clickKey) === true) {
      console.log(`🚫 BLOCKED at button level: ${clickKey}`);
      return;
    }
    
    handleQtyChangeRef.current(index, -1);
  }, []);

  const handlePlusClick = useCallback((index: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any default behavior
    
    const clickKey = `${itemRef.current.id}-${index}-1`;
    console.log(`🔥 Plus button clicked for portion ${index}, key: ${clickKey}`);
    
    // Check lock IMMEDIATELY, before any async operations
    if (globalActiveLocks.get(clickKey) === true) {
      console.log(`🚫 BLOCKED at button level: ${clickKey}`);
      return;
    }
    
    handleQtyChangeRef.current(index, 1);
  }, []);

  const total = quantities.reduce((sum, p) => sum + p.qty * p.price, 0);
  const totalQty = quantities.reduce((sum, p) => sum + p.qty, 0);

  // Check if this instance is the primary one
  const isPrimaryInstance = globalPrimaryInstances.get(item.id) === instanceId;
  
  console.log(`🎯 SmartPortionDropdown rendering - Instance: ${instanceId}, Is Primary: ${isPrimaryInstance}`);
  console.log(`🎯 Quantities:`, quantities.map(p => `${p.name}: ${p.qty}`));


  return (
    <div
      ref={dropdownRef}
      key={`dropdown-${renderKey}`}
      className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] shadow-2xl w-[90vw] md:w-auto md:min-w-[360px] md:max-w-md max-w-[95vw] flex flex-col overflow-hidden ${isModernTheme ? 'rounded-2xl' : 'rounded-xl border border-gray-200'}`}
      style={{
        pointerEvents: isPrimaryInstance ? 'auto' : 'none',
        opacity: isPrimaryInstance ? 1 : 0.5,
        WebkitOverflowScrolling: 'touch',
        maxHeight: 'calc(85vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        paddingTop: 'max(0px, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(0px, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(0px, env(safe-area-inset-right, 0px))',
        boxSizing: 'border-box',
        background: isModernTheme ? 'white' : 'white',
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🛡️ SmartPortionDropdown container clicked - preventing propagation');
      }}
    >
      {/* HEADER - Modern Theme has gradient */}
      <div 
        className="flex-shrink-0 px-4 pt-4 pb-3"
        style={isModernTheme ? { 
          background: itemColor?.gradient,
          borderBottom: 'none'
        } : {
          borderBottom: '1px solid var(--border-subtle)'
        }}
      >
        <div className="flex justify-between items-center">
          <h3 
            className={`font-bold pr-2 ${isModernTheme ? 'text-lg' : 'text-base md:text-lg'}`}
            style={isModernTheme ? { color: itemColor?.color } : { color: 'var(--text-primary)' }}
          >
            {item.name}
          </h3>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              console.log('🧪 CLOSE BUTTON CLICKED!');
              onClose();
            }}
            className={`text-2xl leading-none flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition ${isModernTheme ? 'bg-white/80 hover:bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            style={isModernTheme ? { color: itemColor?.color } : {}}
          >
            ×
          </button>
        </div>
        {isModernTheme && (
          <p className="text-sm mt-1 opacity-70" style={{ color: itemColor?.color }}>
            Select portion sizes
          </p>
        )}
      </div>

      {/* SCROLLABLE MIDDLE */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-white">
        {quantities.map((portion, index) => (
          <div
            key={portion.name}
            className={`flex justify-between items-center gap-3 rounded-xl px-4 py-3 mb-2 transition-all ${isModernTheme ? 'hover:shadow-md' : ''}`}
            style={isModernTheme ? {
              background: portion.qty > 0 ? `${itemColor?.color}10` : '#f8fafc',
              border: portion.qty > 0 ? `2px solid ${itemColor?.color}30` : '1px solid #e2e8f0'
            } : {
              background: 'var(--bg-sunken)'
            }}
          >
            {/* Portion Name */}
            <span 
              className={`flex-1 font-semibold text-sm md:text-base truncate ${isModernTheme && portion.qty > 0 ? '' : ''}`}
              style={isModernTheme && portion.qty > 0 ? { color: itemColor?.color } : { color: 'var(--text-secondary)' }}
            >
              {portion.name}
            </span>
            
            {/* Quantity Controls */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleMinusClick(index)}
                className="w-9 h-9 flex items-center justify-center rounded-full transition font-bold text-lg"
                style={{ 
                  pointerEvents: 'auto', 
                  zIndex: 10000, 
                  background: isModernTheme ? '#f1f5f9' : '#e5e7eb',
                  color: isModernTheme ? itemColor?.color : 'var(--text-secondary)',
                  border: 'none'
                }}
              >
                −
              </button>
              <span
                key={`qty-${index}-${portion.qty}`}
                className="w-8 text-center font-bold text-lg"
                style={{ color: isModernTheme ? itemColor?.color : 'var(--text-primary)' }}
              >
                {portion.qty}
              </span>
              <button
                type="button"
                onClick={handlePlusClick(index)}
                className="w-9 h-9 flex items-center justify-center rounded-full transition font-bold text-lg text-white"
                style={{ 
                  pointerEvents: 'auto', 
                  zIndex: 10000, 
                  background: isModernTheme ? itemColor?.color : 'var(--text-primary)',
                  border: 'none'
                }}
              >
                +
              </button>
            </div>
            
            {/* Price */}
            <span 
              className="font-bold text-sm md:text-base whitespace-nowrap"
              style={{ color: isModernTheme ? itemColor?.color : 'var(--text-secondary)' }}
            >
              {CURRENCY.symbol}{portion.price.toFixed(0)}
            </span>
          </div>
        ))}
        
        {/* Spice Level Selector */}
        {item.allowsSpiceCustomization && totalQty > 0 && (
          <div className="mt-1 pt-3 border-t border-gray-100">
            <SpiceSelector
              selectedLevel={selectedSpiceLevel}
              onChange={setSelectedSpiceLevel}
              size="small"
            />
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div 
        className="flex-shrink-0 px-4 pb-4 pt-3 bg-white"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-500">Total</span>
            <p 
              className="font-bold text-xl"
              style={{ color: isModernTheme ? itemColor?.color : 'var(--text-primary)' }}
            >
              {CURRENCY.symbol}{total.toFixed(0)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                console.log("❌ CANCEL clicked - reverting to cart state");
                const existingQuantities = item.portions.map((portion, index) => {
                  const cartItems = state?.items?.filter(cartItem =>
                    cartItem.menuItem.id === item.id &&
                    cartItem.portionId === portion.id
                  ) || [];
                  const cartQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);
                  return { ...portion, qty: cartQty };
                });
                setQuantities(existingQuantities);
                onClose();
              }}
              className={`px-5 py-2.5 rounded-xl font-semibold transition ${isModernTheme ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log("✅ ADD TO CART clicked - saving selections");
                removeFromCartRef.current(itemRef.current.id);
                quantities.forEach((p) => {
                  if (p.qty > 0) {
                    addToCartRef.current({
                      id: itemRef.current.id,
                      price: p.price,
                      portionId: p.id,
                      portionName: p.name,
                      requestedSpiceLevel: selectedSpiceLevel || undefined,
                    }, p.qty);
                  }
                });
                onClose();
              }}
              className="px-5 py-2.5 rounded-xl font-semibold text-white transition shadow-md hover:shadow-lg"
              style={{ 
                background: isModernTheme ? itemColor?.color : 'var(--status-ok)',
              }}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders from parent
export default memo(SmartPortionDropdown, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders when props haven't meaningfully changed
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.portions.length === nextProps.item.portions.length &&
    prevProps.item.portions.every((portion, index) => 
      portion.name === nextProps.item.portions[index].name &&
      portion.price === nextProps.item.portions[index].price
    ) &&
    prevProps.onClose === nextProps.onClose
  );
});
